import { autoAnimate } from '@formkit/auto-animate';
import { useCallback, useRef, type MutableRefObject } from 'react';

// auto-animate's default keyframe duration, plus a small buffer so we restore scrolling only after
// the last keyframe has settled.
const CLIP_DURATION_MS = 300;

function restorePendingClip(
  clippedElementRef: MutableRefObject<HTMLElement | null>,
  timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>,
): void {
  if (timeoutRef.current) {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }
  clippedElementRef.current?.style.removeProperty('overflow');
  clippedElementRef.current = null;
}

/**
 * A Strict Mode-safe callback ref for auto-animate.
 *
 * The package React hook stores its controller in state, so Strict Mode can replay the callback ref
 * before the first controller is owned by an effect cleanup. Owning it synchronously ensures there is
 * never more than one MutationObserver attached to the same element.
 *
 * While a transition is running, auto-animate absolutely-positions the leaving element at its old
 * offset and animates the remaining rows up from below their final position. Both briefly overhang
 * the element's box and flash a scrollbar on the nearest scroll ancestor. We clip the element for the
 * animation's duration so the transient overflow is hidden, then restore it — a genuinely too-tall
 * container still scrolls once the animation settles.
 */
export function useAutoAnimateRef<T extends HTMLElement>(enabled = true): (element: T | null) => void {
  const controllerRef = useRef<ReturnType<typeof autoAnimate> | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clippedElementRef = useRef<HTMLElement | null>(null);

  return useCallback(
    (element: T | null): void => {
      // Restore any clip we applied before tearing the observer down. The restore otherwise only runs
      // inside the pending timeout, so cancelling it here (e.g. when `enabled` changes mid-clip and
      // React replays the ref) would leave the element stuck with an inline `overflow: clip`.
      restorePendingClip(clippedElementRef, timeoutRef);
      controllerRef.current?.destroy?.();
      observerRef.current?.disconnect();
      observerRef.current = null;

      controllerRef.current = element ? autoAnimate(element) : null;
      if (!element) return;
      if (enabled) controllerRef.current?.enable();
      else controllerRef.current?.disable();

      const observer = new MutationObserver(() => {
        if (!enabled) return;
        element.style.overflow = 'clip';
        clippedElementRef.current = element;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          element.style.removeProperty('overflow');
          clippedElementRef.current = null;
          timeoutRef.current = null;
        }, CLIP_DURATION_MS);
      });
      observer.observe(element, { childList: true });
      observerRef.current = observer;
    },
    [enabled],
  );
}
